import React from "react";
import { Card } from "./Card";

type TableShellProps = {
  title?: React.ReactNode | null;
  subtitle?: React.ReactNode | null;
  right?: React.ReactNode | null;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
};

/**
 * TableShell
 * ----------
 * - Standalone (has title/subtitle/right): render a real <Card> so header background matches.
 * - Embedded (no title/subtitle/right): render flat wrapper only (avoid nested Card).
 */
export function TableShell({
  title = null,
  subtitle = null,
  right = null,
  children,
  className = "",
  bodyClassName = "",
}: TableShellProps) {
  const wantsHeader = Boolean(title || subtitle || right);

  // Standalone → exact same visuals as Card everywhere else
  if (wantsHeader) {
    return (
      <Card
        title={title ?? undefined}
        subtitle={subtitle ?? undefined}
        right={right ?? undefined}
        className={className}
        bodyClassName={bodyClassName || ""}
      >
        <div style={{ overflowX: "auto" }}>{children}</div>
      </Card>
    );
  }

  // Embedded → no extra surface, just table scrolling
  return (
    <div className={`tableShell ${className}`.trim()}>
      <div className={bodyClassName} style={{ overflowX: "auto" }}>
        {children}
      </div>
    </div>
  );
}

/**
 * CellClamp
 * ----------
 * Lightweight line clamp utility used across your ledger tables.
 */
export function CellClamp({
  lines = 2,
  title,
  className = "",
  children,
}: {
  lines?: number;
  title?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={className}
      title={title}
      style={{
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical" as any,
        overflow: "hidden",
        lineHeight: "1.25rem",
      }}
    >
      {children}
    </div>
  );
}
